import { mongoObjectId } from "services/objectid";
import Immutable, { Map } from "immutable";
import moment from "moment";
import { scheduleRuleRow, ScheduleRecord } from "../reducer";

const ERROR_MESSAGE_START_END_TIMES_INVALID = "End time must be later than start time";
const ERROR_MESSAGE_START_END_TIMES_ARE_EQUAL = "Start and end times cannot be the same";
const ERROR_MESSAGE_START_END_TIMES_MUST_NOT_BE_EMPTY = "Start and end times cannot be empty";
const ERROR_MESSAGE_RULE_CONFLICTS = "This rule conflicts with an existing rule’s times";
const ERROR_MESSAGE_NO_DAYS_SELECTED = "Rule must have selected days";

/**
 * @param {Immutable.List} normalOperatingDays the rows of rules that you see in the schedule
 * settings modal
 * @returns {Immutable.List}
 */
const resetConflictingDays = normalOperatingDays => (
  normalOperatingDays.map(rule => rule.set("conflictingDays",
    Immutable.Map({
      days: Immutable.Set(),
      errorMessage: ""
    })))
);

/**
 * Returns the conflicting days if and only if there is a conflict
 * i.e., Monday 9am-5pm, and Monday, 3pm-7pm
 *
 * @param {Immutable.Map} rule1
 * @param {Immutable.Map} rule2
 * @returns {Boolean|Immutable.Set}
 */
export const isConflictBetweenRules = (rule1, rule2) => {
  const intersectingDays = rule1
    .get("days")
    .toSet()
    .intersect(rule2.get("days").toSet());

  // early return if there's no common days between rules
  if (!intersectingDays.size) {
    return false;
  }

  // there is always a conflict if one of the days is "allDay"
  if (rule1.get("allDay") || rule2.get("allDay")) {
    return intersectingDays;
  }

  const startDate1 = moment(rule1.get("start"), "HH:mm");
  const endDate1 = moment(rule1.get("end"), "HH:mm");

  const startDate2 = moment(rule2.get("start"), "HH:mm");
  const endDate2 = moment(rule2.get("end"), "HH:mm");

  const checkRange1 = (startDate1.diff(endDate2) < 0);
  const checkRange2 = (startDate2.diff(endDate1) < 0);

  return checkRange1 && checkRange2 && intersectingDays;
};

/**
 * @param {Immutable.List} normalOperatingDays the rows of rules that you see in the schedule
 * settings modal
 * @param {Object} action
 * @returns {Immutable.List} validated rules
 */
export const validateConflictingRules = (normalOperatingDays, action) => {
  let validatedRules = resetConflictingDays(normalOperatingDays);

  if (normalOperatingDays.size <= 1) {
    return validatedRules;
  }

  // loop through each item in `rules`, compare to the "rest" of the items.
  // this is an O(nlogn) operation

  validatedRules.forEach((rule1, i) => {
    let conflictingDays = validatedRules.getIn([i, "conflictingDays", "days"]);

    validatedRules.slice(i + 1).forEach((rule2, j) => {
      let conflictingDaysOverflow = validatedRules.getIn([j + i + 1, "conflictingDays", "days"]);
      const conflictsBetweenRules = isConflictBetweenRules(rule1, rule2);

      if (conflictsBetweenRules) {
        conflictingDays = conflictingDays.union(conflictsBetweenRules);
        conflictingDaysOverflow = conflictingDaysOverflow.union(conflictsBetweenRules);
        validatedRules = validatedRules.setIn([i, "conflictingDays", "days"], conflictingDays);
        validatedRules = validatedRules.setIn([j + i + 1, "conflictingDays", "days"],
          conflictingDaysOverflow.union(conflictingDays));
      }
    });
  });

  if (action && validatedRules.getIn([action.payload.index, "conflictingDays", "days"]).size) {
    validatedRules = validatedRules.setIn([action.payload.index, "conflictingDays", "errorMessage"],
      ERROR_MESSAGE_RULE_CONFLICTS);
  }

  return validatedRules;
};

/**
 * This function validates the start/end times of a normal day rule in a schedule.
 * It then updates the invalidFields field on the scheduleRow with error info.
 *
 * @param {Immutable.Map} scheduleRow
 * @returns {Immutable.Map}
 */
export const validateStartEndTime = (scheduleRow) => {
  let invalidFields = Immutable.List();
  let validatedNormalDayRow = scheduleRow;

  const start = moment(scheduleRow.get("start"), "HH:mm");
  const end = moment(scheduleRow.get("end"), "HH:mm");

  // Check the start and end times
  if ((start.diff(end) === 0) && !scheduleRow.get("allDay")) {
    // If start and end times are equal
    invalidFields = invalidFields.push(Map({ "field": "startEnd", "errorMessage": ERROR_MESSAGE_START_END_TIMES_ARE_EQUAL }));
  } else if ((start.diff(end) > 0) && !scheduleRow.get("allDay")) {
    // If the start time is later than the end time
    invalidFields = invalidFields.push(Map({ "field": "startEnd", "errorMessage": ERROR_MESSAGE_START_END_TIMES_INVALID }));
  } else if ((!scheduleRow.get("start") || !scheduleRow.get("end")) && !scheduleRow.get("allDay")) {
    // If either of the start or end times are not set
    invalidFields = invalidFields.push(Map({ "field": "startEnd", "errorMessage": ERROR_MESSAGE_START_END_TIMES_MUST_NOT_BE_EMPTY }));
  }

  validatedNormalDayRow = validatedNormalDayRow.set("invalidFields", invalidFields);

  return validatedNormalDayRow;
};

/**
 * @param {Immutable.List} rules a list of schedule rules
 * @param {Array} requiredFields
 * @returns {Boolean}
 */
const scheduleRulesHaveRequiredFields = (rules, requiredFields) => (
  rules.every(
    rule => requiredFields.every(
      field => Boolean(rule.get(field).size)
    )
  )
);

const hasNameAndTimezone = state => (
  state.name && state.timezone
);

/**
 * @param {ScheduleRecord} state The unvalidated schedule record
 * @param {Object} action
 * @returns {Object}
 */
const validateNormalOperatingHours = (state, action) => {
  let validatedNormalHours = state.get("normalOperatingDaysRows");
  let isValid = true;
  let hasErrors = false;

  // Validate all start/end times
  validatedNormalHours = validateConflictingRules(
    validatedNormalHours.map(
      rule => validateStartEndTime(rule)
    ), action
  );

  // make sure the `days` field is not empty
  validatedNormalHours = validatedNormalHours.map((row) => {
    let validatedRow = row;

    if (validatedRow.get("days").isEmpty()) {
      validatedRow = validatedRow.update("invalidFields",
        invalidFields => invalidFields.push(Map({ "field": "days", "errorMessage": ERROR_MESSAGE_NO_DAYS_SELECTED })));
    }

    return validatedRow;
  });

  const isRulesNonEmpty = Boolean(validatedNormalHours.size);

  const hasRequiredFields = scheduleRulesHaveRequiredFields(
    validatedNormalHours,
    ["days"]
  );

  const hasInvalidFields = validatedNormalHours
    .findIndex(row => row.get("invalidFields").size) >= 0;

  const hasConflictingDays = validatedNormalHours
    .findIndex(row => row.getIn(["conflictingDays", "days"]) && row.getIn(["conflictingDays", "days"]).size) >= 0;

  if (!hasRequiredFields || hasInvalidFields || hasConflictingDays || !isRulesNonEmpty) {
    isValid = false;
    hasErrors = true;
  }

  return { validatedNormalHours, isValid, hasErrors };
};

/**
 *
 * @param {Immutable.Map} state
 * @returns {Object}
 */
const validateSpecialOperatingHours = (state) => {
  let validatedSpecialHours = state.get("specialOperatingDaysRows");
  let isValid = true;
  let hasErrors = false;

  validatedSpecialHours = validatedSpecialHours.map(rule => validateStartEndTime(rule));

  const isDateInvalid = Boolean(validatedSpecialHours.get("date"));
  const rowHasInvalidFields = validatedSpecialHours
    .findIndex(row => row.get("invalidFields").size) >= 0;

  if (isDateInvalid || rowHasInvalidFields) {
    isValid = false;
  }

  if (rowHasInvalidFields) {
    hasErrors = true;
  }

  return { validatedSpecialHours, isValid, hasErrors };
};

/**
 * Validates the schedule rows for a schedule record
 * @param {ScheduleRecord} state The unvalidated schedule record
 * @param {Object} action
 * @returns {ScheduleRecord} the validated schedule record
 */
const validateScheduleRecord = (state, action) => {
  let validatedState = state;

  const {
    validatedNormalHours,
    isValid: normalRowsValid,
    hasErrors: normalRowsHaveErrors
  } = validateNormalOperatingHours(validatedState, action);

  const {
    validatedSpecialHours,
    isValid: specialRowsValid,
    hasErrors: specialRowsHaveErrors
  } = validateSpecialOperatingHours(validatedState, action);

  validatedState = validatedState.set("normalOperatingDaysRows", validatedNormalHours);
  validatedState = validatedState.set("specialOperatingDaysRows", validatedSpecialHours);

  validatedState = validatedState
    .set("isValid",
      Boolean(validatedState.get("name") &&
      validatedState.get("timezone") &&
      normalRowsValid &&
      specialRowsValid))
    .set("hasErrors", normalRowsHaveErrors || specialRowsHaveErrors);

  return validatedState;
};

export const settingsScheduleModal = (state = new ScheduleRecord(), action) => {
  let nextState = state;

  switch (action.type) {
    case "CREATE_SETTINGS_SCHEDULE_MODAL":
      nextState = new ScheduleRecord();

      return nextState.set("id", mongoObjectId());

    case "SET_SETTINGS_SCHEDULE_MODAL": {
      const { payload } = action;
      const newScheduleRecord = new ScheduleRecord(payload);

      return newScheduleRecord;
    }

    case "UPDATE_SETTINGS_SCHEDULE_MODAL": {
      nextState = state.merge(action.payload);

      const validatedScheduleIsValid = validateScheduleRecord(nextState).isValid;

      if (validatedScheduleIsValid) {
        nextState = nextState.set("isValid", hasNameAndTimezone(nextState));
      }

      return nextState;
    }

    case "ADD_SETTINGS_SCHEDULE_ROW": {
      // The current local time
      const now = moment();
      // Get the remainder to round up to 30m
      const minuteReminder = 30 - (now.minute() % 30);
      // add remainder to start and for
      const start = now.add(minuteReminder, "minutes").format("HH:mm");
      const end = now.add(1, "hours").format("HH:mm");

      // Set the start and end time of the row we're adding
      const row = scheduleRuleRow
        .set("start", start)
        .set("end", end);

      // update state
      nextState = nextState.update(action.payload.scheduleType, rows => rows.push(row));

      // state is no longer valid
      nextState = nextState.set("isValid", false);

      return nextState;
    }

    case "DELETE_SETTINGS_SCHEDULE_ROW": {
      nextState = state.update(
        action.payload.scheduleType,
        rows => rows.delete(action.payload.index)
      );

      return validateScheduleRecord(nextState);
    }

    case "UPDATE_SETTINGS_SCHEDULE_ROW": {
      nextState = state.mergeIn(
        [action.payload.scheduleType, action.payload.index],
        action.payload.value
      );

      return validateScheduleRecord(nextState, action);
    }

    default:
      return state;
  }
};
